import * as Sentry from "@sentry/nextjs"
import { RequestDeduplicator } from "@/governance/helpers/RequestDeduplicator"
import {
  type DRSConfig,
  type DRSInfo,
  type DRSProvider,
  DRSStatus,
} from "@/governance/types/drs"

/**
 * Base class for DRS providers with common functionality
 */
export abstract class BaseDRSProvider implements DRSProvider {
  protected config: DRSConfig
  private deduplicator = new RequestDeduplicator()

  constructor(config: DRSConfig) {
    this.config = config
  }

  /**
   * Initialize the provider
   */
  async initialize(): Promise<this> {
    return this
  }

  /**
   * Get DRS info for a single address
   */
  async getDRSInfo(address: string): Promise<DRSInfo | null> {
    const key = `single-${this.config.name}-${address.toLowerCase()}`

    return this.deduplicator.dedupe(key, async () => {
      try {
        return await this.fetchSingleAddress(address)
      } catch (error) {
        this.logError("getDRSInfo", error, { address })
        return this.createEmptyDRSInfo(address)
      }
    })
  }

  /**
   * Get DRS info for multiple addresses
   */
  async getBulkDRSInfo(addresses: string[]): Promise<Map<string, DRSInfo>> {
    if (addresses.length === 0) return new Map()

    // Create a stable key for deduplication
    const sortedAddresses = [...addresses].sort()
    const key = `bulk-${this.config.name}-${sortedAddresses.join(",")}`

    return this.deduplicator.dedupe(key, async () => {
      try {
        return await this.fetchBulkAddresses(addresses)
      } catch (error) {
        this.logError("getBulkDRSInfo", error, {
          addressCount: addresses.length,
        })
        const result = new Map<string, DRSInfo>()
        addresses.forEach((address) => {
          result.set(address.toLowerCase(), this.createEmptyDRSInfo(address))
        })
        return result
      }
    })
  }

  /**
   * Log error to Sentry and console
   */
  protected logError(
    method: string,
    error: unknown,
    extra?: Record<string, unknown>,
  ): void {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.error(`${this.constructor.name}.${method}:`, errorMessage)

    Sentry.captureException(error, {
      level: "error",
      tags: {
        provider: this.config.provider,
        method,
      },
      extra: {
        drsName: this.config.name,
        errorMessage,
        ...extra,
      },
    })
  }

  /**
   * Create empty DRS info for an address
   */
  protected createEmptyDRSInfo(address: string): DRSInfo {
    return {
      address: address.toLowerCase(),
      status: DRSStatus.INACTIVE,
      drsScore: 0,
      lastUpdated: null,
      externalLink: this.buildExternalLink(address),
      provider: this.config.provider,
      isEligible: false,
      hasInfo: false,
      probationSupported: this.getProbationSupport(),
      isProbation: false,
      username: null,
    }
  }

  /**
   * Build external profile link
   */
  protected buildExternalLink(address: string): string {
    return `${this.config.profileBaseUrl}/${address}`
  }

  // Abstract methods to be implemented by providers
  protected abstract fetchSingleAddress(
    address: string,
  ): Promise<DRSInfo | null>

  protected abstract fetchBulkAddresses(
    addresses: string[],
  ): Promise<Map<string, DRSInfo>>

  protected abstract getProbationSupport(): boolean
}
